import React, { useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

const COUNTRIES = [
  { code: 'IN', dial: '91',  flag: '🇮🇳', name: 'India' },
  { code: 'US', dial: '1',   flag: '🇺🇸', name: 'United States' },
  { code: 'GB', dial: '44',  flag: '🇬🇧', name: 'United Kingdom' },
  { code: 'AE', dial: '971', flag: '🇦🇪', name: 'UAE' },
  { code: 'SA', dial: '966', flag: '🇸🇦', name: 'Saudi Arabia' },
  { code: 'QA', dial: '974', flag: '🇶🇦', name: 'Qatar' },
  { code: 'KW', dial: '965', flag: '🇰🇼', name: 'Kuwait' },
  { code: 'BH', dial: '973', flag: '🇧🇭', name: 'Bahrain' },
  { code: 'OM', dial: '968', flag: '🇴🇲', name: 'Oman' },
  { code: 'PK', dial: '92',  flag: '🇵🇰', name: 'Pakistan' },
  { code: 'BD', dial: '880', flag: '🇧🇩', name: 'Bangladesh' },
  { code: 'LK', dial: '94',  flag: '🇱🇰', name: 'Sri Lanka' },
  { code: 'NP', dial: '977', flag: '🇳🇵', name: 'Nepal' },
  { code: 'SG', dial: '65',  flag: '🇸🇬', name: 'Singapore' },
  { code: 'MY', dial: '60',  flag: '🇲🇾', name: 'Malaysia' },
  { code: 'AU', dial: '61',  flag: '🇦🇺', name: 'Australia' },
  { code: 'CA', dial: '1',   flag: '🇨🇦', name: 'Canada' },
  { code: 'DE', dial: '49',  flag: '🇩🇪', name: 'Germany' },
  { code: 'FR', dial: '33',  flag: '🇫🇷', name: 'France' },
  { code: 'ZA', dial: '27',  flag: '🇿🇦', name: 'South Africa' },
  { code: 'NG', dial: '234', flag: '🇳🇬', name: 'Nigeria' },
  { code: 'KE', dial: '254', flag: '🇰🇪', name: 'Kenya' },
];

/**
 * PhoneInput — country-code selector + local number field.
 *
 * Props:
 *   value      {string}   full digits-only number (e.g. "919876543210")
 *   onChange   {fn}       called with full digits-only number on every change
 *   required   {bool}
 *   disabled   {bool}
 *   placeholder {string}  overrides default local-number placeholder
 */
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

function parseValue(v) {
  const normalized = String(v || '').replace(/\D/g, '');
  const matchedCountry = [...COUNTRIES]
    .sort((a, b) => b.dial.length - a.dial.length)
    .find((country) => normalized.startsWith(country.dial));

  if (matchedCountry) {
    return { dial: matchedCountry.dial, local: normalized.slice(matchedCountry.dial.length) };
  }

  return { dial: '91', local: normalized };
}

export default function PhoneInput({
  value = '',
  onChange,
  required,
  disabled,
  placeholder,
  label,
  error,
  hint,
  className,
  inputClassName,
}) {
  const parsed = useMemo(() => parseValue(value), [value]);
  const [dial, setDial] = useState(parsed.dial);
  const [local, setLocal] = useState(parsed.local);

  useEffect(() => {
    setDial(parsed.dial);
    setLocal(parsed.local);
  }, [parsed.dial, parsed.local]);

  const country = COUNTRIES.find(c => c.dial === dial) || COUNTRIES[0];

  const handleDialChange = (e) => {
    const newDial = e.target.value;
    setDial(newDial);
    onChange?.(newDial + local.replace(/\D/g, ''));
  };

  const handleLocalChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '');
    setLocal(digits);
    onChange?.(dial + digits);
  };

  return (
    <div className={cn('space-y-2 w-full group', className)}>
      {label ? (
        <label
          className="text-[12px] font-medium transition-colors ml-0.5"
          style={{ color: error ? '#e50000' : 'var(--color-text-main)' }}
        >
          {label}
        </label>
      ) : null}

      <div
        className="flex h-9 rounded-[var(--radius-slds)] overflow-hidden transition-all duration-150"
        style={{
          backgroundColor: 'var(--color-brand-card)',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: error ? '#BA0517' : '#D8DDE6',
        }}
      >
        <div className="flex items-center bg-[#F4F6F9] border-r border-[#D8DDE6] px-2 relative min-w-[76px]">
          <span className="text-[14px] mr-1 z-10 pointer-events-none">{country.flag}</span>
          <select
            value={dial}
            onChange={handleDialChange}
            disabled={disabled}
            aria-label="Country code"
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
          >
            {COUNTRIES.map(c => (
              <option key={c.code + c.dial} value={c.dial}>
                {c.flag} {c.name} (+{c.dial})
              </option>
            ))}
          </select>
          <span className="text-[12px] font-semibold text-[#444444] z-10 pointer-events-none ml-1">+{dial}</span>
        </div>
        <input
          className={cn(
            'flex-1 w-full bg-transparent border-none outline-none px-3 text-[13px] text-[#181818] placeholder:text-[#B0ADAB] disabled:opacity-50',
            inputClassName
          )}
          type="tel"
          value={local}
          onChange={handleLocalChange}
          placeholder={placeholder || '98765 43210'}
          required={required}
          disabled={disabled}
          inputMode="numeric"
        />
      </div>

      {error ? (
        <div className="text-[11px] font-medium ml-0.5 animate-in slide-in-from-top-1" style={{ color: '#e50000' }}>
          {error}
        </div>
      ) : null}
      {hint && !error ? (
        <div className="text-[11px] font-medium ml-0.5" style={{ color: 'var(--color-text-muted)' }}>
          {hint}
        </div>
      ) : null}
    </div>
  );
}
